import { useEffect, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { useLive } from '@/lib/useLive';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** 即時觀看面板：畫面 + 狀態列 + 聲音開關。
 *  掛載即開始、卸載即釋放租約（useLive 內處理）。 */
export function LivePanel({ deviceKey }: { deviceKey: string }) {
  const { videoRef, status, start, stop } = useLive();
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    void start(deviceKey);
    return () => stop();
    // deviceKey 變了要重新開一路
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceKey]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted, videoRef]);

  return (
    <div className="space-y-4">
      <div className="relative overflow-hidden rounded-md bg-slate-900">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          className="aspect-video w-full bg-slate-900 object-contain"
          autoPlay
          playsInline
          muted
        />
        {status.kind !== 'live' && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <span className="rounded-sm bg-slate-900/80 px-3 py-1.5 text-[12.5px] text-slate-200">
              {status.message}
            </span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              status.kind === 'live' && 'bg-success',
              status.kind === 'busy' && 'bg-warn motion-safe:animate-pulse',
              status.kind === 'error' && 'bg-destructive',
              status.kind === 'idle' && 'bg-muted-foreground',
            )}
          />
          <span
            className="text-[12.5px] text-muted-foreground"
            data-live-status={status.kind}
            data-live-transport={status.transport}
          >
            {status.message}
          </span>
        </div>

        <Button variant="secondary" size="sm" onClick={() => setMuted((m) => !m)}>
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          <span>{muted ? '開啟聲音' : '靜音'}</span>
        </Button>
      </div>

      <p className="text-[11.5px] leading-relaxed text-muted-foreground">
        優先使用 WebRTC（延遲最低）。若連線環境不允許直連，會自動改用 HLS 播放，
        延遲約數秒。關閉此面板即釋放觀看租約，裝置會停止推流。
      </p>
    </div>
  );
}
